import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject} from '@angular/core';
import {DraftsService, PatientDraft} from 'src/app/core/services/drafts.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {CoreService} from 'src/app/core/services/core.service';
import {debounceTime, distinctUntilChanged, map} from 'rxjs/operators';
import {AppMatAutocompleteComponent} from '@xmedical/ui';
import {MedicationItemAddCardComponent} from '../medication-item-add-card/medication-item-add-card.component';
import {MatDialog} from '@angular/material/dialog';
import {DraftInteractionBehaviour} from 'src/app/core/layout/draft-overlay/draft-interaction-behaviour';
import {Script, Medication, MedicationRepositoryService} from '@xmedical/core';
import {PatientScriptsStore} from '../../../core/state';

@Component({
    selector: 'app-create-script',
    templateUrl: './create-script.component.html',
    styleUrls: ['./create-script.component.scss'],
    standalone: false
})
export class CreateScriptComponent implements OnInit, OnDestroy, DraftInteractionBehaviour {

  @Input() draft: PatientDraft;
  form: UntypedFormGroup = null;
  script: Script = null;
  loading = false;
  saving = false;
  @ViewChild(AppMatAutocompleteComponent) autocomplete: AppMatAutocompleteComponent;
  private subscriptions: Subscription[] = [];
  private triggerTextChanges = false;
  private debounceTimeOut = 1500;

  private scriptsStore = inject(PatientScriptsStore);

  constructor(
    private dialog: MatDialog,
    private draftService: DraftsService,
    private formBuilder: UntypedFormBuilder,
    private coreService: CoreService,
    private medicationRepository: MedicationRepositoryService
  ) {
  }

  get saveButtonEnabled(): boolean {
    return this.formValid && !this.saving;
  }

  get formValid(): boolean {
    return this.form && this.form.valid;
  }

  get isEditing(): boolean {
    return this.draft.draftId !== null && this.script !== null;
  }

  get hasMedication(): boolean {
    return this.medication.length > 0;
  }

  get medication(): Medication[] {
    return this.script.medicationList;
  }

  searchMedication$ = (query: string): Observable<Medication[]> => {
    return this.medicationRepository.query(query)
      .pipe(map(response => response));
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      patientName: ['', Validators.required],
      scriptDate: [new Date(), Validators.required],
      medicalAidName: [''],
      medicalAidNumber: [''],
    });
    this.getScriptDetails();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  onAddNewItem() {
    const DialogRef = this.dialog.open(MedicationItemAddCardComponent, { panelClass: 'mat-confirmation-dialog-responsive'});
    this.subscriptions.push(DialogRef.afterClosed().subscribe(result => {
      console.log(result);
    }));
  }

  medicationItemChanged() {
    this.formChanged();
  }

  onItemSelected(medication: Medication) {
    this.script.medicationList.push(medication);
    this.autocomplete.cancelSearch();
    this.formChanged();
  }

  save() {
    this.formChanged(true);
  }

  getScriptDetails() {
    this.loading = true;
    this.subscriptions.push(this.scriptsStore.load(this.draft.draftId).subscribe({
      next: script => {
        this.script = script;
        this.dataLoaded();
      },
      error: error => {
        console.error(error);
      }
    }));
  }


  confirmationMessage(item: Medication): string {
    return 'Are you sure you want to remove ' + item.description + ' this script?';
  }

  public removeMedicationFromScript(index: number) {
    this.script.medicationList.splice(index, 1);
    this.formChanged();
  }

  private dataLoaded(): void {
    this.handleExistingScriptDataLoaded();
    this.subscribeToFormChanges();
    this.loading = false;
  }

  private handleExistingScriptDataLoaded(): void {
    this.form.get('patientName').setValue(this.script.patientName);
    this.form.get('scriptDate').setValue(this.script.scriptDate);
    this.form.get('medicalAidName').setValue(this.script.medicalAidName);
    this.form.get('medicalAidNumber').setValue(this.script.medicalAidNumber);
    this.form.updateValueAndValidity();
    this.triggerTextChanges = true;
  }

  private subscribeToFormChanges(): void {
    this.subscriptions.push(this.form.valueChanges.pipe(debounceTime(this.debounceTimeOut), distinctUntilChanged()).subscribe(() => {
      this.formChanged();
    }));
  }

  private formChanged(locked: boolean = false) {
    if (this.saving) {
      return;
    }
    if (this.form.valid) {
      this.updateScript(locked);
    }
  }

  private updateScript(locked: boolean): void {
    const updateScript = this.form.value as Script;
    const scriptDate = this.form.get('scriptDate').value;
    updateScript.scriptDate = new Date(scriptDate);
    updateScript.medicationList = this.script.medicationList;
    updateScript.locked = locked;
    updateScript.duration = 0;
    updateScript.scriptType = this.script.scriptType;
    this.subscriptions.push(this.scriptsStore.update(this.script.id, updateScript).subscribe({
      next: () => {
        if (updateScript.locked) {
          this.draftService.closeDraft(this.draft.draftId, this.draft.draftType);
        }
        this.saving = false;
      },
      error: error => {
        console.error(error);
        this.saving = false;
      }
    }));
  }
}
